/**
 * PATH: packages/api/src/moneyDocuments.ts
 * FILE_ID: API-MONEYDOC-CONTRACTS-001
 * PURPOSE: Canonical frontend contracts for native money documents and template packs.
 * WHY: Claims, invoice menus, and settings must share one backend-shaped contract.
 */

import type { BackendSchemas } from './contracts'

export const KNOWN_MONEY_DOCUMENT_ACTIONS = [
  'open',
  'edit',
  'submit',
  'approve',
  'reject',
  'download_source',
  'download_pdf',
  'download_docx',
  'send',
  'resend',
  'mark_paid',
  'set_status',
  'void',
] as const

export type KnownMoneyDocumentAction =
  (typeof KNOWN_MONEY_DOCUMENT_ACTIONS)[number]
export type MoneyDocumentAction =
  | KnownMoneyDocumentAction
  | (string & {})

export interface MoneyDocumentActionSource {
  available_actions?: readonly MoneyDocumentAction[] | null
}

/** Server-advertised actions are authoritative; absent fields retain legacy behavior. */
export function hasMoneyDocumentAction(
  source: MoneyDocumentActionSource,
  action: MoneyDocumentAction,
  legacyFallback = false,
): boolean {
  if (source.available_actions == null) return legacyFallback
  return source.available_actions.includes(action)
}

export type MoneyDocumentArtifactFormat = 'pdf' | 'docx'

export interface MoneyDocumentArtifact {
  id: string
  format: MoneyDocumentArtifactFormat
  file: string
  file_name: string
  download_url: string | null
  checksum: string
  snapshot_checksum: string
  template_pack_version: string | null
  generated_at: string
  metadata: Record<string, unknown>
}

export type MoneyDocumentEvidenceKind = 'file' | 'email'

export interface MoneyDocumentEvidence {
  id: string
  file: string | null
  file_name: string | null
  file_content_type: string | null
  file_size: number | null
  email: string | null
  email_subject: string | null
  source_type: MoneyDocumentEvidenceKind | 'missing'
  is_missing: boolean
  caption: string
  sort_order: number
  include_in_document: boolean
  include_on_send: boolean
  added_by_name: string | null
  added_at: string
  updated_at: string
}

export interface AddMoneyDocumentEvidenceInput {
  file_record_id?: string
  email_id?: string
  file?: File
  caption?: string
  sort_order?: number
  include_in_document?: boolean
  include_on_send?: boolean
}

export interface UpdateMoneyDocumentEvidenceInput {
  caption?: string
  sort_order?: number
  include_in_document?: boolean
  include_on_send?: boolean
}

export interface MoneyDocumentEvidenceManifestItem {
  id: string
  kind: MoneyDocumentEvidenceKind
  source_id: string
  name: string
  content_type: string
  checksum: string
  caption: string
  sort_order: number
  include_in_document: boolean
  include_on_send: boolean
}

export interface MoneyDocumentSummary extends MoneyDocumentActionSource {
  id: string
  doc_type: string
  display_name: string
  period: string
  claim_number: number
  legal_number: string
  external_ref: string
  status: string
  this_claim: string | number
  created_at: string
  updated_at: string
  source: string
  has_snapshot: boolean
  latest_artifact?: MoneyDocumentArtifact | null
}

export interface MoneyDocumentDetail extends MoneyDocumentSummary {
  artifacts: MoneyDocumentArtifact[]
  evidence_manifest_snapshot: MoneyDocumentEvidenceManifestItem[]
  snapshot_checksum: string
  snapshot_created_at: string | null
  template_pack_version: string | null
}

export type DocumentTemplatePackDocumentType =
  BackendSchemas['DocumentTypeC42Enum']
export type DocumentTemplatePackStatus = BackendSchemas['StatusDaeEnum']

export interface DocumentTemplatePackConfig {
  brand_name?: string
  brand_color?: string
  ref_prefix?: string
  sopa_statement_text?: string
  footer_text?: string
  payment_terms?: string
  bank_details?: string
  show_sopa_statement?: boolean
  show_evidence_appendix?: boolean
}

export type DocumentTemplatePack = Omit<
  BackendSchemas['DocumentTemplatePack'],
  'document_type' | 'config'
> & {
  document_type: DocumentTemplatePackDocumentType
  config: DocumentTemplatePackConfig
}

export interface CreateDocumentTemplatePackInput {
  name: string
  document_type: DocumentTemplatePackDocumentType
  config: DocumentTemplatePackConfig
}

export interface UpdateDocumentTemplatePackInput {
  name?: string
  document_type?: DocumentTemplatePackDocumentType
  config?: DocumentTemplatePackConfig
}
